import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, CheckCircle, Phone, ArrowLeft } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge, AnimatedCard, CardHeader, CardTitle, CardDescription } from '@/components/ui'
import { AssessmentRequestForm } from '@/components/sections'
import { Assessment } from '@/data/assessments'
import { companyInfo } from '@/data/company'

interface AssessmentDetailPageProps {
  assessment: Assessment
}

export function AssessmentDetailPage({ assessment }: AssessmentDetailPageProps) {
  return (
    <>
      <SEO
        title={`${assessment.title} | Free Assessment`}
        description={assessment.longDescription}
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8"
          >
            <Link 
              to="/assessments" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-accent-400 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              All Assessments
            </Link>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="glow" className="mb-6">{assessment.shortTitle} Assessment</Badge>
              
              <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
                {assessment.title}
              </h1>

              <p className="text-lg md:text-xl text-slate-300 mb-4">
                {assessment.tagline}
              </p>

              <p className="text-slate-400 mb-8">
                {assessment.longDescription}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                {assessment.externalUrl ? (
                  <a href={assessment.externalUrl} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="glow" className="group">
                      {assessment.externalCtaText || 'Start Your Assessment'}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                ) : (
                  <a href="#request-form">
                    <Button size="lg" variant="glow" className="group">
                      Request This Assessment
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                )}
                <a href={`tel:${companyInfo.phoneRaw}`}>
                  <Button size="lg" variant="secondary">
                    <Phone className="w-5 h-5" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Visual Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="glass-card p-8 rounded-3xl">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${assessment.bgGradient} flex items-center justify-center mb-6 mx-auto`}>
                  <assessment.icon className={`w-10 h-10 ${assessment.colorClass}`} />
                </div>
                <h3 className="text-xl font-heading font-semibold text-white text-center mb-6">
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {assessment.deliverables.map((deliverable, index) => (
                    <motion.li
                      key={deliverable}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.4 }}
                      className="flex items-center gap-3"
                    >
                      <CheckCircle className={`w-5 h-5 ${assessment.colorClass} flex-shrink-0`} />
                      <span className="text-slate-300">{deliverable}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <Badge className="mb-4">Benefits</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Why Get This Assessment?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assessment.benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start gap-4"
              >
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${assessment.bgGradient} flex items-center justify-center flex-shrink-0`}>
                  <CheckCircle className={`w-5 h-5 ${assessment.colorClass}`} />
                </div>
                <p className="text-slate-300">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Our Process</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              How the Assessment Works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {assessment.processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                {/* Connector line */}
                {index < assessment.processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-accent-500/50 to-transparent -translate-x-4" />
                )}
                
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${assessment.bgGradient} flex items-center justify-center mb-4`}>
                  <span className={`text-2xl font-heading font-bold ${assessment.colorClass}`}>
                    {(index + 1).toString().padStart(2, '0')}
                  </span>
                </div>
                <h3 className="text-lg font-heading font-semibold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-400 text-sm">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">What We Evaluate</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Assessment Focus Areas
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {assessment.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AnimatedCard className="h-full">
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${assessment.bgGradient} flex items-center justify-center mb-4`}>
                      <feature.icon className={`w-7 h-7 ${assessment.colorClass}`} />
                    </div>
                    <CardTitle>{feature.title}</CardTitle>
                    <CardDescription className="text-base">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Form Section */}
      <section id="request-form" className="section-padding scroll-mt-32">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="glow" className="mb-4">Get Started</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
                {assessment.externalUrl ? `Get Your Free ${assessment.shortTitle} Assessment` : `Request Your Free ${assessment.shortTitle} Assessment`}
              </h2>
              <p className="text-lg text-slate-400 mb-6">
                {assessment.externalUrl 
                  ? `Start the online assessment now for immediate insights, or fill out the form and our experts will contact you within 24 hours.`
                  : 'Fill out the form and one of our experts will contact you within 24 hours to schedule your complimentary assessment.'
                }
              </p>
              
              <div className="glass-card p-6 rounded-xl mb-6">
                <h4 className="text-lg font-heading font-semibold text-white mb-4">
                  You'll Receive:
                </h4>
                <ul className="space-y-3">
                  {assessment.deliverables.slice(0, 3).map((item) => (
                    <li key={item} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className={`w-5 h-5 ${assessment.colorClass}`} />
                      {item}
                    </li>
                  ))}
                  <li className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className={`w-5 h-5 ${assessment.colorClass}`} />
                    Plus personalized recommendations
                  </li>
                </ul>
              </div>

              <p className="text-sm text-slate-500">
                Questions? Call us at{' '}
                <a href={`tel:${companyInfo.phoneRaw}`} className="text-accent-400 hover:underline">
                  {companyInfo.phone}
                </a>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {assessment.externalUrl && (
                <div className="glass-card p-6 rounded-2xl text-center border-2 border-accent-500/30">
                  <h3 className="text-lg font-heading font-semibold text-white mb-3">
                    Start Your Free Assessment Now
                  </h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Our online assessment takes just a few minutes and provides immediate insights.
                  </p>
                  <a href={assessment.externalUrl} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="glow" className="group w-full">
                      {assessment.externalCtaText || 'Start Your Assessment'}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                </div>
              )}
              <div className="glass-card p-8 rounded-2xl">
                {assessment.externalUrl && (
                  <p className="text-center text-slate-400 text-sm mb-6">
                    Or have our team reach out to you:
                  </p>
                )}
                <AssessmentRequestForm
                  assessmentType={assessment.id}
                  assessmentTitle={assessment.title}
                  buttonText={`Request ${assessment.shortTitle} Assessment`}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Assessments */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-display-sm font-heading font-bold text-white mb-4">
              Explore Other Assessments
            </h2>
            <p className="text-slate-400">
              We offer a full suite of technology assessments to help optimize your organization.
            </p>
          </motion.div>

          <div className="flex justify-center">
            <Link to="/assessments">
              <Button size="lg" variant="secondary" className="group">
                View All Assessments
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
